import { useState } from "react";
import { Button, ErrorNote, useCanWrite } from "@/components/ui";
import { DeleteForever } from "@/components/delete";
import { DataTable, type DataTableColumn } from "@/components/table";
import { api, errorMessage } from "@/lib/api";
import { useArchivedFirms, useRefreshDirectory } from "@/lib/queries";
import type { Preset, PropFirm } from "@/lib/types";

/*
 * GLI ARCHIVIATI di Prop firm e di Broker, e da qui si ripristinano.
 *
 * Archiviare non cancella: la riga sparisce dagli elenchi e i cicli che la
 * leggono continuano a leggerla. Senza questa sezione il ritorno esisteva solo
 * come rotta che nessuna schermata raggiungeva.
 *
 * Il ripristino è UNO PER VOLTA, com'è nel backend: la firm torna SENZA i suoi
 * programmi (alcuni potevano essere archiviati da prima, e riportarli tutti
 * riporterebbe indietro anche quelli), e un programma non torna sotto una firm
 * archiviata — prima lei. Una firm attiva compare qui solo per i suoi programmi
 * archiviati.
 *
 * Sta nella pagina «Archiviati» di Prop firm e di Broker (`pages/Archived.tsx`),
 * non più in fondo all'elenco. Se l'elenco non si legge lo si dice, non si lascia
 * credere che non ci sia niente.
 */

export function ArchivedFirms({ kind }: { kind: "PROP" | "BROKER" }) {
  const canWrite = useCanWrite();
  const archived = useArchivedFirms(kind);
  const refresh = useRefreshDirectory();
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (archived.isLoading) return <p className="px-6 py-6 text-sm text-muted">Caricamento…</p>;
  if (archived.error) {
    return (
      <div className="px-6 py-6">
        <ErrorNote>Archiviati non leggibili: {errorMessage(archived.error)}</ErrorNote>
      </div>
    );
  }
  const rows = archived.data ?? [];
  const isProp = kind === "PROP";

  const restore = async (key: string, fn: () => Promise<unknown>) => {
    setBusy(key);
    setError(null);
    try {
      await fn();
      await refresh();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(null);
    }
  };

  const columns: DataTableColumn<PropFirm>[] = [
    { key: "name", header: isProp ? "Prop firm" : "Broker", sortBy: (f) => f.name, cell: (f) => <span className="font-medium text-ink">{f.name}</span> },
    {
      key: "state",
      header: "Stato",
      cell: (f) =>
        f.isArchived ? (
          <span className="text-muted">Archiviata</span>
        ) : (
          <span className="text-muted">Attiva, con programmi archiviati</span>
        ),
    },
    { key: "presets", header: "Programmi archiviati", align: "right", cell: (f) => f.presets.length },
    {
      key: "actions",
      header: <span className="sr-only">Azioni</span>,
      align: "right",
      cell: (f) =>
        canWrite && f.isArchived ? (
          <span className="inline-flex flex-wrap items-center justify-end gap-2">
            <Button size="sm" disabled={busy === f.id} onClick={() => void restore(f.id, () => api.propFirms.restore(f.id))}>
              {busy === f.id ? "Ripristino…" : "Ripristina"}
            </Button>
            <DeleteForever kind={isProp ? "firm" : "broker"} id={f.id} label={f.name} size="sm" />
          </span>
        ) : null,
    },
  ];

  return (
    <section className="px-6 py-4">
      <div className="card">
        <p className="px-4 py-2 text-xs text-faint">
          {isProp
            ? "Una firm ripristinata torna senza i suoi programmi: si ripristinano uno per uno, dopo di lei. I cicli che li usano non hanno mai smesso di leggerli."
            : "Un broker ripristinato riprende i suoi server, che intanto stavano in «Server senza possessore»."}
        </p>
        {error && (
          <div className="px-4 pb-2">
            <ErrorNote>{error}</ErrorNote>
          </div>
        )}
        <DataTable
          bare
          columns={columns}
          rows={rows}
          rowKey={(f) => f.id}
          empty={{ title: "Nessun archiviato", hint: "Ciò che archivi compare qui, e da qui torna." }}
          expandedContent={(f) =>
            f.presets.length === 0 ? (
              <p className="text-sm text-muted">{isProp ? "Nessun programma." : "Un broker non ha programmi."}</p>
            ) : (
              <ArchivedPresets firm={f} canWrite={canWrite} busy={busy} onRestore={(p) => void restore(p.id, () => api.propFirms.restorePreset(p.id))} />
            )
          }
        />
      </div>
    </section>
  );
}

function ArchivedPresets({
  firm,
  canWrite,
  busy,
  onRestore,
}: {
  firm: PropFirm;
  canWrite: boolean;
  busy: string | null;
  onRestore: (p: Preset) => void;
}) {
  return (
    <table className="w-full text-sm">
      <tbody className="tabular-nums">
        {firm.presets.map((p) => (
          <tr key={p.id} className="border-b border-line/60 last:border-0">
            <td className="px-4 py-2 font-medium text-ink">{p.name}</td>
            <td className="py-2 pr-4 text-right text-muted">{Number(p.accountSize).toLocaleString("it-IT", { maximumFractionDigits: 0 })}</td>
            <td className="py-2 pr-4 text-right">
              {canWrite && (
                <span className="inline-flex flex-wrap items-center justify-end gap-2">
                  {firm.isArchived ? (
                    // Il backend lo rifiuta (FIRM_ARCHIVED): qui non si offre e si dice perché.
                    <span className="text-xs text-faint">ripristina prima la firm</span>
                  ) : (
                    <Button size="sm" disabled={busy === p.id} onClick={() => onRestore(p)}>
                      {busy === p.id ? "Ripristino…" : "Ripristina"}
                    </Button>
                  )}
                  <DeleteForever kind="programma" id={p.id} label={p.name} size="sm" />
                </span>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
